/**
 * useMultiCurrencyInfo Hook
 * 
 * Detecta quando múltiplas empresas com moedas diferentes estão selecionadas
 * e fornece informações úteis para exibição e conversão.
 * 
 * @returns Informações sobre as moedas das empresas selecionadas
 * 
 * @example
 * ```tsx
 * const { hasMultipleCurrencies, baseCurrency, currencies, currencyCount } = useMultiCurrencyInfo();
 * 
 * if (hasMultipleCurrencies) {
 *   // Mostrar indicador de conversão
 * }
 * ```
 */

import { useMemo } from 'react';
import { useCompanyContext } from '../contexts/CompanyContext';
import { DEFAULT_CURRENCY } from '../lib/constants/currencies';
import type { Currency } from '../types/currency';

export interface MultiCurrencyInfo {
  /** Se há empresas com moedas diferentes selecionadas */
  hasMultipleCurrencies: boolean;
  
  /** Moeda base (da primeira empresa) usada para consolidação */
  baseCurrency: Currency;
  
  /** Lista de todas as moedas únicas das empresas selecionadas */
  currencies: Currency[];
  
  /** Quantidade de moedas diferentes */
  currencyCount: number;
  
  /** Mapa de empresas por moeda para breakdown detalhado */
  companiesByCurrency: Map<Currency, Array<{ id: string; name: string }>>;
}

export function useMultiCurrencyInfo(): MultiCurrencyInfo {
  const { selectedCompanies } = useCompanyContext();

  return useMemo(() => {
    // Se não há empresas selecionadas
    if (selectedCompanies.length === 0) {
      return {
        hasMultipleCurrencies: false,
        baseCurrency: DEFAULT_CURRENCY,
        currencies: [DEFAULT_CURRENCY],
        currencyCount: 1,
        companiesByCurrency: new Map(),
      };
    }

    // Moeda base = moeda da primeira empresa
    const baseCurrency: Currency = selectedCompanies[0]?.default_currency || DEFAULT_CURRENCY;

    // Agrupar empresas por moeda
    const companiesByCurrency = new Map<Currency, Array<{ id: string; name: string }>>();
    
    selectedCompanies.forEach((company) => {
      const currency = company.default_currency || DEFAULT_CURRENCY;
      const companyInfo = { id: company.id, name: company.name };
      
      if (!companiesByCurrency.has(currency)) {
        companiesByCurrency.set(currency, []);
      }
      companiesByCurrency.get(currency)!.push(companyInfo);
    });

    // Lista de moedas únicas
    const currencies = Array.from(companiesByCurrency.keys());
    const currencyCount = currencies.length;

    // Há múltiplas moedas se existir mais de uma moeda única
    const hasMultipleCurrencies = currencyCount > 1;

    return {
      hasMultipleCurrencies,
      baseCurrency,
      currencies,
      currencyCount,
      companiesByCurrency,
    };
  }, [selectedCompanies]);
}

